import * as React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { filterBy } from "@progress/kendo-data-query";
import { useNominalsBySubType } from "services/nominalsService";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Form, FormElement, Field } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import * as api from "../../api/index";
import { AmountFormatCell, DateFormatCell } from "components/common/formatter";
import { useState, useEffect, useRef } from "react";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import { NameTextCell } from "components/common/GridComponents";
import { Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { useActiveVats } from "services/vatService";
import { Button } from "@progress/kendo-react-buttons";
import styles from "./Bills.module.scss";
import { GridLoader } from "components/GridLoader";

const ValidatedComboBox = ({
  label,
  value,
  isRequired = true,
  showValidation,
  textField,
  ...comboBoxProps
}) => {
  const getValidationValue = () => {
    if (textField === "display") {
      // For nominal ref, check if value has nominalRef property
      return value?.nominalRef;
    }
    // For simple values like vatvalue
    return value;
  };

  const hasError = showValidation && isRequired && !getValidationValue();

  const width = comboBoxProps.style?.width || "300px";

  return (
    <div className="d-flex flex-column mb-3" style={{ width }}>
      <Label>{label}</Label>
      <ComboBox
        {...comboBoxProps}
        textField={textField}
        style={{
          width,
          border: hasError ? "1px solid red" : "",
          borderRadius: "4px",
          ...comboBoxProps.style
        }}
        className="mt-1"
        value={value}
      />
    </div>
  );
};

const MattersTimeRecords = (props) => {
  const [dataAvailable, setDataAvailable] = useState(false);
  const [error, setError] = useState(undefined);
  const [error1, setError1] = useState(undefined);
  const [nominal, setnominal] = useState();
  const [nominaldata, setnominaldata] = React.useState();
  const [data, setData] = React.useState([]);
  const [matters, setMatters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [vatvalue, setvatvalue] = useState();
  const [vatsdata, setvatsData] = React.useState();
  const [showValidation, setShowValidation] = useState(false);
  const [selectedFeeEarner, setSelectedFeeEarner] = useState(null);

  const [summaryUnits, setSummaryUnits] = useState(0);
  const [summaryValue, setSummaryValue] = useState(0);
  const [summaryBillTo, setSummaryBillTo] = useState(0);

  const hasInitializedVat = useRef(false);

  const { data: activevatsdata = [] } = useActiveVats();

  const getSelectedRecordsSummary = () => {
    const selectedRecords = data.filter(item => item.add === true);
    const count = selectedRecords.length;
    const totalUnits = selectedRecords.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = selectedRecords.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalBillVat = selectedRecords.reduce((sum, item) => sum + (item.billVat || 0), 0);

    return { count, totalUnits, totalValue, totalBillVat };
  };

  useEffect(() => {
    const selectedRecords = data.filter(item => item.add === true);
    const totalUnits = selectedRecords.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = selectedRecords.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalBillVat = selectedRecords.reduce((sum, item) => sum + (item.billVat || 0), 0);

    setSummaryUnits(totalUnits);
    setSummaryValue(totalValue);
    setSummaryBillTo(totalValue);
  }, [data]);

  const getOverallTotals = () => {
    const totalUnits = data.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);

    return { totalUnits, totalValue };
  };


  const AmountNameHeader = (props) => {
    return (
      <div style={{ textAlign: "center" }}>
        {props.title}
      </div>
    );
  };

  const TotalFooterCell = (props) => {
    return (isLoading ? null :
      <td style={{
        textAlign: "center",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #a1baff",
        borderBottom: "none",
      }}>
        {props.children}
      </td>
    );
  };

  const TotalLabelFooterCell = () => {
    return (isLoading ? null :
      <td style={{ fontWeight: "bold", backgroundColor: "#f8f9fa", borderTop: "1px solid #a1baff" }}>
        Total
      </td>
    )
  };

  const EmptyFooterCell = () => {
    return (isLoading ? null :
      <td style={{
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #a1baff"
      }}>
      </td>
    )
  };

  const BillNetCell = (props) => {
    const value = props.dataItem.billedNetAmount;

    if (value === "w/o" || value === null || value === undefined) {
      return (
        <td style={{ textAlign: "center" }}>
          w/o
        </td>
      );
    }

    return (
      <td style={{ textAlign: "center" }}>
        {typeof value === 'number' ? value.toFixed(2) : value}
      </td>
    );
  };

  const onChangenominal = (event) => {
    let selectedNominal = event.value;
    if (selectedNominal && selectedNominal.display) {
      const parts = selectedNominal.display.split(' - ');
      const firstPart = parts[0]?.trim();


      selectedNominal = {
        ...selectedNominal,
        display: firstPart
      };
    }

    setnominal(selectedNominal);

    // Only apply to currently selected records
    if (data.length > 0 && selectedNominal) {
      const updatedData = data.map((item) => {
        if (!item.add) return item; // Skip unselected records
        return { ...item, nominalRef: selectedNominal.nominalRef };
      });
      setData(updatedData);
    }
    // If nominal is cleared (null), don't change anything - keep existing values
  };

  const calculateVatForRecords = (records, vatCode, selectedOnly = false) => {
    if (!vatCode || !vatCode.rate) {
      return records.map((item) => {
        if (selectedOnly && !item.add) return item;
        return { ...item, billVat: 0 };
      });
    }
    const vatRate = vatCode.rate / 100;
    return records.map((item) => {
      if (selectedOnly && !item.add) return item;
      const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
      return {
        ...item,
        billVat: parseFloat(((netAmount || 0) * vatRate).toFixed(2)),
      };
    });
  };

  const adjustVatPennyDifferences = (records) => {
    const selectedRecords = records.filter(item => item.add === true);

    if (selectedRecords.length === 0) return records;

    const vatGroups = {};
    selectedRecords.forEach(record => {

      let vatRate = 0;
      const netAmount = typeof record.billedNetAmount === 'number' ? record.billedNetAmount : record.value;
      if (netAmount && netAmount !== 0) {
        vatRate = (record.billVat || 0) / netAmount;
      }
      const vatRateKey = vatRate.toFixed(4);

      if (!vatGroups[vatRateKey]) {
        vatGroups[vatRateKey] = {
          rate: vatRate,
          records: []
        };
      }
      vatGroups[vatRateKey].records.push(record);
    });

    // Process each VAT group
    Object.keys(vatGroups).forEach(vatRateKey => {
      const group = vatGroups[vatRateKey].records;
      const vatRate = vatGroups[vatRateKey].rate;

      if (group.length === 0) return;


      // Step 1: Calculate sum of individual rounded VAT amounts
      const sumOfIndividualVat = group.reduce((sum, item) => {
        const itemVat = parseFloat((item.billVat || 0).toFixed(2));
        return sum + itemVat;
      }, 0);

      // Step 2: Calculate VAT on total billedNetAmount
      const totalValue = group.reduce((sum, item) => {
        const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
        return sum + (netAmount || 0);
      }, 0);
      const vatOnTotal = parseFloat((totalValue * vatRate).toFixed(2));


      // Step 3: Calculate the difference
      const difference = parseFloat((sumOfIndividualVat - vatOnTotal).toFixed(2));

      // Step 4: Adjust the last record in the group if there's a difference
      if (difference !== 0 && group.length > 0) {
        const lastRecord = group[group.length - 1];
        const recordIndex = records.findIndex(r => r.timeId === lastRecord.timeId);

        if (recordIndex !== -1) {
          const oldVat = records[recordIndex].billVat;
          const newVat = parseFloat((oldVat - difference).toFixed(2));
          records[recordIndex] = {
            ...records[recordIndex],
            billVat: newVat
          };
        }
      }
    });

    return records;
  };

  const onChangevat = (event) => {
    let selectedVat = event.value;

    if (selectedVat && selectedVat.display) {
      const parts = selectedVat.display.split(' - ');
      const firstPart = parts[0]?.trim();
      const lastPart = parts[parts.length - 1]?.trim();

      selectedVat = {
        ...selectedVat,
        display: `${firstPart} - ${lastPart}`
      };
    }

    setvatvalue(selectedVat);

    if (selectedVat && data.length > 0) {
      const updatedData = calculateVatForRecords(data, selectedVat, true);
      setData(updatedData);
    }
  };

  const filterData = (filter) => {
    const datas = activevatsdata.slice();
    return filterBy(datas, filter);
  };

  const filterChange = (event) => {
    setvatsData(filterData(event.filter));
  };

  const filternominalData = (filter) => {
    const data = nominallist.slice();
    return filterBy(data, filter);
  };

  const filternominal = (event) => {
    setnominaldata(filternominalData(event.filter));
  };

  const { data: incomeNominals } = useNominalsBySubType("ProfitAndLoss_Income");
  const nominallist = incomeNominals || [];


  const handleSubmit = (values) => {
    setShowValidation(true);

    // if (!vatvalue || !nominal?.nominalRef) {
    //   setError(true);
    //   return;
    // }

    // Check if at least one record is selected
    const hasSelectedRecords = data.some(item => item.add === true);
    if (!hasSelectedRecords) {
      setError1(true);
      return;
    }

    const adjustedData = adjustVatPennyDifferences([...data]);
    const dataToPost = adjustedData.filter(item => item.add === true);

    props.changeMatterList(dataToPost);
    props.addNominalVatInfo({
      nominalRef: nominal,
      vatCode: vatvalue,
      feeEarnerRef: values.feeEarner ? values.feeEarner.userRef : null,
    });

    props.cancelEdit();
  };

  const itemChange = (e) => {
    let newData = data.map((item) => {
      if (item.timeId === e.dataItem.timeId) {
        const updatedItem = { ...item, add: e.value };

        if (e.value) {
          if (vatvalue && vatvalue.rate) {
            const vatRate = vatvalue.rate / 100;
            // Use billedNetAmount if it's a number, otherwise use value
            const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
            updatedItem.billVat = parseFloat(((netAmount || 0) * vatRate).toFixed(2));
          }

          if (nominal && nominal.nominalRef) {
            updatedItem.nominalRef = nominal.nominalRef;
          }

          if (selectedFeeEarner && selectedFeeEarner.userRef) {
            updatedItem.billFeeEarnerRef = selectedFeeEarner.userRef;
          }
        }

        return updatedItem;
      }
      return item;
    });
    setData(newData);
  };

  const addDefaultValues = async () => {
    console.log(props.defaultValues?.defaultVatCodeId);
    const defaultVat = await activevatsdata.find(
      (item) => item.vatCodeId === props.defaultValues?.defaultVatCodeId
    );
    if (defaultVat) {
      let selectedVat = defaultVat;

      if (selectedVat && selectedVat.display) {
        const parts = selectedVat.display.split(' - ');
        const firstPart = parts[0]?.trim();
        const lastPart = parts[parts.length - 1]?.trim();

        selectedVat = {
          ...selectedVat,
          display: `${firstPart} - ${lastPart}`
        };
      }
      setvatvalue(selectedVat);
      setData((prevData) => {
        if (prevData.length > 0) {
          return calculateVatForRecords(prevData, defaultVat, true);
        }
        return prevData;
      });
    }
  };



  const fetchNominal = async () => {

    setnominaldata(incomeNominals?.slice());
    let nominal = incomeNominals?.find(
      (item) => item.nominalRef == props.defaultValues?.defaultProfitNominal
    )

    if (nominal && nominal.display) {
      const parts = nominal.display.split(' - ');
      const firstPart = parts[0]?.trim();

      nominal = {
        ...nominal,
        display: firstPart
      };
      setnominal(nominal);
    } else {
      setnominal(null);
    }
  };

  useEffect(() => {
    const getMatterTimeRecordsData = async () => {
      setIsLoading(true);
      try {
        const response = await api.getMatterTimeRecords(props.matterId);
        const allRecords = response.data?.data || [];

        // Filter out records that are already in the parent grid
        const existingTimeIds = (props.existingRecords || []).map(r => r.timeId);
        const filteredRecords = allRecords.filter(
          record => !existingTimeIds.includes(record.timeId)
        );

        setMatters(
          filteredRecords.map((record) => ({
            inEdit: true,
            type: "Time",
            billVat: 0,
            add: false,
            ...record,
            originalNominalRef: record.nominalRef,
            nominalRef: props.defaultValues?.defaultProfitNominal || record.nominalRef, // Set to default
            billFeeEarnerRef: record.feeEarnerRef, // Default Bill FE to FE
            billedNetAmount: record.value, // Initialize Bill Net with value
          }))
        );
      } catch (error) {
        console.error("Error fetching matter time records:", error);
        setMatters([]);
      } finally {
        setIsLoading(false);
      }
    };

    getMatterTimeRecordsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.matterId]);

  const SelectionHeaderCell = (headerProps) => {
    return (
      <div className="gap-2 d-flex">
        <span>{headerProps.title}</span>
        <Checkbox
          style={{
            width: "18px",
            height: "18px",
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
          }}
          onChange={(e) => {
            if (e.value) {
              setData((prevData) =>
                prevData.map((item) => {
                  const updatedItem = { ...item, add: true };

                  if (vatvalue && vatvalue.rate) {
                    const vatRate = vatvalue.rate / 100;
                    // Use billedNetAmount if it's a number, otherwise use value
                    const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
                    updatedItem.billVat = parseFloat(((netAmount || 0) * vatRate).toFixed(2));
                  }

                  if (nominal && nominal.nominalRef) {
                    updatedItem.nominalRef = nominal.nominalRef;
                  }

                  if (selectedFeeEarner && selectedFeeEarner.userRef) {
                    updatedItem.billFeeEarnerRef = selectedFeeEarner.userRef;
                  }

                  return updatedItem;
                })
              );
            } else {
              setData((prevData) =>
                prevData.map((item) => ({
                  ...item,
                  add: false
                }))
              );
            }
          }}
          name="isCounselFee"
          value={data.filter((item) => !item.add).length === 0}
        />
      </div>
    );
  };

  useEffect(() => {
    if (
      activevatsdata?.length > 0 &&
      props?.defaultValues?.defaultVatCodeId &&
      !vatvalue &&
      !hasInitializedVat.current
    ) {
      setvatsData(activevatsdata.slice());
      addDefaultValues();
      hasInitializedVat.current = true; // Mark as initialized
    }
  }, [activevatsdata]);

  useEffect(() => {
    fetchNominal();
  }, []);


  const requiredValidator = (value) => {
    return value ? undefined : "invalid"; // triggers k-invalid class for Fee Earner field
  };

  useEffect(() => {
    setTimeout(() => {
      if (
        matters[0] !== null &&
        matters[0] !== "LIST_EMPTY" &&
        matters[0] !== undefined &&
        dataAvailable === false
      ) {
        setData(matters);
        setDataAvailable(true);
      } else if (matters[0] === "LIST_EMPTY") {
        setData([]);
        setDataAvailable(false);
      }
    }, 0);
  }, [matters]);

  const summary = getSelectedRecordsSummary();
  const overallTotals = getOverallTotals();


  const handleWriteOff = () => {
    const updatedData = data.map((item) => {
      if (item.add === true) {
        return { ...item, billedNetAmount: "w/o", billVat: 0 };
      }
      return item;
    });
    setData(updatedData);
    setTimeout(() => setSummaryBillTo(0), 0);
  };

  const handleApplyBillTo = () => {
    const selectedRecords = data.filter(item => item.add === true);
    if (selectedRecords.length === 0) {
      return;
    }


    // Step 1: Calculate total original billedNetAmount (use originalBilledNetAmount if exists, else use value)
    const totalOriginalValue = selectedRecords.reduce((sum, item) => {
      const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;
      return sum + (origValue || 0);
    }, 0);

    // Step 2: Calculate proportional values for each selected record
    // If all records have zero value, distribute equally instead
    const isAllZero = totalOriginalValue === 0;
    const equalShare = isAllZero ? summaryBillTo / selectedRecords.length : 0;

    let updatedData = data.map((item) => {
      if (!item.add) return item;

      // Preserve original billedNetAmount on first application
      const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;

      let newBilledNetAmount;
      if (isAllZero) {
        // Distribute equally if all values are zero
        newBilledNetAmount = equalShare;
      } else {
        // Formula: (Original Value / Total Original Value) × "Bill To Amount"
        const proportion = (origValue || 0) / totalOriginalValue;
        newBilledNetAmount = proportion * summaryBillTo;
      }

      const roundedNewBilledNetAmount = parseFloat(newBilledNetAmount.toFixed(2));


      return {
        ...item,
        billedNetAmount: roundedNewBilledNetAmount,           // Update the billedNetAmount field (not value)
        originalBilledNetAmount: origValue,         // Preserve original billedNetAmount
      };
    });

    // Step 3: Adjust penny difference in last selected record
    const updatedSelectedRecords = updatedData.filter(item => item.add === true);
    const sumOfRoundedValues = updatedSelectedRecords.reduce((sum, item) => sum + (item.billedNetAmount || 0), 0);
    const pennyDifference = parseFloat((summaryBillTo - sumOfRoundedValues).toFixed(2));

    if (pennyDifference !== 0 && updatedSelectedRecords.length > 0) {
      const lastRecord = updatedSelectedRecords[updatedSelectedRecords.length - 1];
      const lastRecordIndex = updatedData.findIndex(r => r.timeId === lastRecord.timeId);

      if (lastRecordIndex !== -1) {
        const oldBilledNetAmount = updatedData[lastRecordIndex].billedNetAmount;
        const newBilledNetAmount = parseFloat((oldBilledNetAmount + pennyDifference).toFixed(2));
        updatedData[lastRecordIndex] = {
          ...updatedData[lastRecordIndex],
          billedNetAmount: newBilledNetAmount
        };
      }
    }

    // Step 4: Recalculate VAT based on new billedNetAmount
    if (vatvalue && vatvalue.rate) {
      const vatRate = vatvalue.rate / 100;
      updatedData = updatedData.map((item) => {
        if (!item.add) return item;
        const newVat = parseFloat(((item.billedNetAmount || 0) * vatRate).toFixed(2));
        return {
          ...item,
          billVat: newVat
        };
      });
    }

    // Step 5: Apply VAT penny adjustment

    const finalData = adjustVatPennyDifferences(updatedData);

    setData(finalData);
    setTimeout(() => setSummaryBillTo(summaryBillTo), 0);

  };
  const getSelectedItemsCount = () => {
    return data.filter(item => item.add === true).length;
  };

  return (
    <Dialog title={`Time Record`} onClose={props.cancelEdit} className="dialog-xxl">
      <div className="d-flex flex-column h-100" >
        <div className="pt-4 pb-3 px-5">
          <div className="p-3 rounded" style={{ boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)" }}>

            <div className="mb-3 border-bottom">
              <h6 className="fw-bold mb-2">Selected Time Records ({summary.count})</h6>
            </div>

            <div className="d-flex flex-wrap align-items-center validation">
              <Form
                ignoreModified={true}
                onSubmit={handleSubmit}
                id="timeRecord"
                initialValues={{
                  feeEarner: null
                }}
                render={(formRenderProps) => {
                  return (
                    <div style={{ width: "100%" }}>
                      <FormElement id="timeRecord" name="timeRecord">
                        <fieldset>
                          {formRenderProps.visited &&
                            formRenderProps.errors &&
                            formRenderProps.errors.VALIDATION_SUMMARY && (
                              <div className={"k-messagebox k-messagebox-error"}>
                                {formRenderProps.errors.VALIDATION_SUMMARY}
                              </div>
                            )}

                          <div className="d-flex justify-content-between">
                            <div className="d-flex gap-1 gap-xxl-2">
                              <ValidatedComboBox
                                label="VAT Code"
                                value={vatvalue}
                                // showValidation={showValidation}
                                name="vatCode"
                                data={activevatsdata}
                                textField="display"
                                filterable={true}
                                onFilterChange={filterChange}
                                iconClassName="wa"
                                onChange={onChangevat}
                                placeholder="Search VAT Code"
                                style={{ width: "150px" }}
                              />

                              <ValidatedComboBox
                                label="Nominal Ref"
                                value={nominal}
                                // showValidation={showValidation}
                                name="nominalRef"
                                data={nominaldata}
                                iconClassName="wa"
                                textField="display"
                                filterable={true}
                                onFilterChange={filternominal}
                                onChange={onChangenominal}
                                placeholder="Search Nominal Ref"
                                style={{ width: "150px" }}
                              />
                              <Field
                                name="feeEarner"
                                component={SearchComboBox}
                                cbType={CONSTANT.FE}
                                customLabel="Allocate To"
                                // validator={requiredValidator}
                                validationMessage=""
                                className="mt-2"
                                iconClassName="wa"
                                width="150px"
                                placeholder="Search Fee Earner"
                                customCombo={true}
                                onChangeFunction={(e) => {
                                  let feeEarner = e.value;

                                  if (feeEarner && feeEarner.display) {
                                    const parts = feeEarner.display.split(' - ');
                                    const firstPart = parts[0]?.trim();

                                    feeEarner = {
                                      ...feeEarner,
                                      display: firstPart
                                    };
                                  }

                                  setSelectedFeeEarner(feeEarner);
                                  formRenderProps.onChange("feeEarner", { value: feeEarner });

                                  // Only apply to currently selected records if fee earner is selected
                                  if (data.length > 0 && feeEarner) {
                                    const updatedData = data.map((item) => {
                                      if (!item.add) return item;
                                      return { ...item, billFeeEarnerRef: feeEarner.userRef };
                                    });
                                    setData(updatedData);
                                  }
                                  // If fee earner is cleared (null), don't change anything - keep existing values
                                }}
                              />
                            </div>

                            <div className="d-flex flex-column align-items-end gap-1">
                              <div className="d-flex gap-3">
                                <div className="d-flex flex-column">
                                  <Label>Total Units</Label>
                                  <NumericTextBox
                                    value={summaryUnits}
                                    onChange={(e) => setSummaryUnits(e.value || 0)}
                                    format="n2"
                                    spinners={false}
                                    className={`mt-1 ${styles['numeric-input']}`}
                                    style={{ width: "103px", height: "30px" }}
                                    disabled={true}
                                  />
                                </div>

                                <div className="d-flex flex-column">
                                  <Label>Total Value</Label>
                                  <NumericTextBox
                                    value={summaryValue}
                                    onChange={(e) => setSummaryValue(e.value || 0)}
                                    format="n2"
                                    spinners={false}
                                    className={`mt-1 ${styles['numeric-input']}`}
                                    style={{ width: "103px", height: "30px" }}
                                    disabled={true}
                                  />
                                </div>
                              </div>

                              <div className="d-flex gap-1 gap-xxl-2">
                                <div className="d-flex flex-column">
                                  <Label>Bill to</Label>
                                  <NumericTextBox
                                    value={summaryBillTo}
                                    onChange={(e) => setSummaryBillTo(e.value || 0)}
                                    format="n2"
                                    spinners={false}
                                    min={0}
                                    className={`mt-1 ${styles['numeric-input']}`}
                                    style={{ width: "75px", height: "30px" }}
                                  />
                                </div>
                                <div className="d-flex gap-1 align-items-end">
                                  <Button
                                    type="button"
                                    className="bgBtnSecondary button-small"
                                    onClick={handleApplyBillTo}
                                    style={{ height: "30px" }}
                                    disabled={!summaryBillTo || summaryBillTo <= 0}
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    type="button"
                                    className="common-btn button-small"
                                    onClick={handleWriteOff}
                                    style={{ height: "30px" }}
                                  >
                                    Write Off
                                  </Button>
                                </div>
                              </div>

                            </div>
                          </div>
                        </fieldset>
                      </FormElement>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex-grow-1 px-5" style={{ minHeight: 0 }}>
          <Grid
            data={data}
            dataItemKey={"timeId"}
            onItemChange={itemChange}
            editField="inEdit"
            className="grid-header-sm grid-minwidth-col4-200 "
            style={{ height: "100%" }}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            showLoader={isLoading}
            loader={
              <div style={{ height: "400px" }}>
                <GridLoader />
              </div>
            }
          >
            <GridNoRecords>
              {isLoading ? (
                <span></span>
              ) : (
                data && data.length === 0 && "No records available"
              )}
            </GridNoRecords>
            <Column
              field="feeEarnerRef"
              title="FE"
              editable={false}
              width={"60"}
              footerCell={TotalLabelFooterCell}
            />
            <Column
              field="billFeeEarnerRef"
              title="Bill FE"
              editable={false}
              width={"70"}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="date"
              title="Date"
              editable={false}
              cell={DateFormatCell}
              width={"100"}
              footerCell={EmptyFooterCell}
            />

            <Column
              field="details"
              title="Details"
              editable={false}
              // width={"300"}
              cell={NameTextCell}
              footerCell={EmptyFooterCell}
            />
            {props.defaultValues?.defaultProfitNominal !== null &&
              (
                <Column
                  field="nominalRef"
                  title="Nominal"
                  editable={false}
                  cell={(cellProps) => {
                    return (
                      <td style={{ textAlign: "left" }}>
                        {cellProps.dataItem.nominalRef || props.defaultValues?.defaultProfitNominal}
                      </td>
                    )
                  }}
                  width={"90"}
                  footerCell={EmptyFooterCell}
                />
              )}
            <Column
              field="units"
              title="Units"
              editable={false}
              width={"63"}
              headerCell={AmountNameHeader}
              cell={(props) => (
                <td style={{ textAlign: "center" }}>
                  {props.dataItem.units}
                </td>
              )}
              footerCell={() => <TotalFooterCell>{overallTotals.totalUnits}</TotalFooterCell>}
            />
            <Column
              field="rate"
              title="Rate"
              editable={false}
              width={"60"}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="value"
              title="Value"
              editable={false}
              width={"90"}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={() => <TotalFooterCell>{overallTotals.totalValue.toFixed(2)}</TotalFooterCell>}
            />
            <Column
              field="billedNetAmount"
              title="Bill Net"
              editable={false}
              cell={BillNetCell}
              headerCell={AmountNameHeader}
              width={100}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="billVat"
              width={"90"}
              title="Vat"
              editable={false}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="add"
              title="Select"
              className="d-flex align-items-center justify-center mt-2 ms-3"
              editor="boolean"
              width={"90px"}
              // headerSelectionValue={true}
              headerCell={SelectionHeaderCell}
              footerCell={EmptyFooterCell}
            />
          </Grid>
        </div>

        <div className="px-5 pb-3">
          {error === true && (
            <i style={{ color: "red" }}>Please fill in all required fields</i>
          )}
          {error1 === true && (getSelectedItemsCount() === 0 && data.length > 0) && (
            <i style={{ color: "red" }}>Select atleast 1 Record</i>
          )}
        </div>
      </div>
      <DialogActionsBar>

        <div className="dialog-footer">
          <button
            className="common-btn bgBtnSecondary border-0 me-3"
            onClick={() => {
              props.cancelEdit();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="timeRecord"
            className="k-button common-btn me-4"
          >
            Add to Bill
          </button>
        </div>
      </DialogActionsBar>

    </Dialog>
  );
};

export default MattersTimeRecords;
